import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { getOrderDetailsCombined } from 'redux/reducers/orders/selectors';
import { getAllCountriesOptions } from 'redux/reducers/settings/selectors';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { TOrderDetailsRowChildProps } from './types';
import { Duties } from './Duties';
import { Total } from './Total';
import { COLORS } from 'services/constants/colors';
import { EasChip } from 'ui/EasChip';

export const OrderDetailsRowChild: FC<TOrderDetailsRowChildProps> = ({
	child,
	paymentCurrency,
	detalization,
}) => {
	const orderDetailsCombined = useSelector(getOrderDetailsCombined);
	const countries = useSelector(getAllCountriesOptions);

	const discount = orderDetailsCombined?.orderDiscounts
		?.flatMap((d) => d?.orderDiscountItems ?? [])
		.find((item) => item?.idProvidedByEm === child?.idProvidedByEm)?.discountPercentage;
	const returned = orderDetailsCombined?.orderReturns
		?.flatMap((r) => r?.orderReturnItems ?? [])
		.filter((item) => item?.idProvidedByEm === child?.idProvidedByEm)
		.reduce((acc, curr) => acc + curr.returnItemQuantity, 0);

	return (
		<Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					gap: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: 1,
					}}
				>
					<Typography variant="body2">
						<span style={{ fontWeight: 'bold' }}>{child.idProvidedByEm} — </span>
						{child.description}
					</Typography>
					{(() => {
						const country = countries?.find(
							(cntr) => cntr?.code === child.locationWarehouseCountry,
						);
						if (!country) return '';
						return (
							<Tooltip title="Warehouse Country">
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										fontSize: '.7rem',
										height: '16px',
									}}
								>
									<CountryWithFlag countryCode={country?.code} size="small" tooltip={false} />
								</Box>
							</Tooltip>
						);
					})()}
					{!!discount && <EasChip label={`-${discount.toFixed(1)} %`} bgcolor={COLORS.DISCOUNT} />}
					{!!returned && (
						<EasChip label={`${String(returned)} returned`} bgcolor={COLORS.RETURNED} />
					)}
				</Box>

				{detalization ? (
					<Box
						sx={{
							display: 'flex',
							gap: 1,
							alignSelf: 'end',
						}}
					>
						<Typography
							variant="body2"
							sx={{
								fontSize: '.8rem',
							}}
						>
							<AmountAndCurrency
								amount={child?.costProvidedByEm ?? 0}
								currency={paymentCurrency}
								size={0.8}
							/>
							&nbsp;&times;&nbsp;{child.quantity}&nbsp;=&nbsp;
						</Typography>
						<AmountAndCurrency
							amount={(child?.costProvidedByEm ?? 0) * (child?.quantity ?? 0)}
							currency={paymentCurrency}
							bold
							size={0.9}
						/>
					</Box>
				) : (
					<Box>
						<AmountAndCurrency
							amount={child?.totalItemCost ?? 0}
							currency={paymentCurrency}
							bold
							size={1}
							color={COLORS.PRIMARY}
						/>
					</Box>
				)}
			</Box>
			{/* <Duties
				duty={child.costProvidedByEm}
				nameOfDuty="Cost Per Item"
				paymentCurrency={paymentCurrency}
			/> */}
			{detalization && (
				<>
					{!!child?.customsDuty && (
						<Duties
							duty={child.customsDuty}
							nameOfDuty={`Customs Duty (${child.customDutyRate}%)`}
							paymentCurrency={paymentCurrency}
						/>
					)}
					<Duties
						duty={child.itemOnlyVat}
						nameOfDuty={`Item VAT (${child.vatRate}%)`}
						paymentCurrency={paymentCurrency}
					/>
					{!!child.proratedDeliveryCost && (
						<Duties
							duty={child.proratedDeliveryCost}
							nameOfDuty="Delivery Charge"
							paymentCurrency={paymentCurrency}
						/>
					)}
					{!!child.itemDeliveryChargeVat && (
						<Duties
							duty={child.itemDeliveryChargeVat}
							nameOfDuty={`VAT on Delivery Charge (${child.vatRate}%)`}
							paymentCurrency={paymentCurrency}
						/>
					)}
					{!!child.proratedEasFee && (
						<Duties
							duty={child.proratedEasFee}
							nameOfDuty="EAS Fee"
							paymentCurrency={paymentCurrency}
						/>
					)}
					{!!child.proratedEasFeeVat && (
						<Duties
							duty={child.proratedEasFeeVat}
							nameOfDuty="VAT on EAS Fee (22%)"
							paymentCurrency={paymentCurrency}
						/>
					)}

					<Divider sx={{ mt: 0.4, mb: 0.4, ml: 4 }} />
					<Total
						totalPerItem={
							child?.costProvidedByEm ?? 0
							// (child?.customsDuty ?? 0) +
							// (child?.itemOnlyVat ?? 0) +
							// (child?.proratedEasFee ?? 0) +
							// (child?.proratedEasFeeVat ?? 0) +
							// (child?.proratedDeliveryCost ?? 0) +
							// (child?.itemDeliveryChargeVat ?? 0)
						}
						quantity={child.quantity}
						totalCost={child.totalItemCost}
						paymentCurrency={paymentCurrency}
					/>
				</>
			)}
		</Box>
	);
};
